import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  FileText, ArrowRight, Search, RefreshCw, Clock,
  CheckCircle, XCircle, AlertCircle, Eye, Calendar,
  Package, Box, ShoppingCart, Filter, ChevronRight,
  Truck, Navigation, MapPin
} from 'lucide-react';

const DocumentFlowDashboard = () => {
  const [flows, setFlows] = useState([]);
  const [filteredFlows, setFilteredFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, complete, incomplete, issues
  const [dateFilter, setDateFilter] = useState('today');
  const [stats, setStats] = useState({
    total: 0,
    complete: 0,
    inProgress: 0,
    withIssues: 0
  });

  useEffect(() => {
    loadDocumentFlows();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadDocumentFlows, 30000);
    return () => clearInterval(interval);
  }, [dateFilter]);

  useEffect(() => {
    filterFlows();
  }, [flows, searchTerm, statusFilter]);

  const loadDocumentFlows = async () => {
    setLoading(true);

    // Build date filter
    let dateFilterQuery = '';
    const now = new Date();
    
    if (dateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      dateFilterQuery = `created_at=gte.${today}T00:00:00`;
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      dateFilterQuery = `created_at=gte.${weekAgo}`;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      dateFilterQuery = `created_at=gte.${monthAgo}`;
    }

    // Fetch orders with their picking and packing tasks
    const { data: orders, error } = await supabase
      .from('orders')
      .select(`
        id,
        order_number,
        status,
        created_at,
        total,
        user_id,
        box_allocation_id,
        delivery_address_id,
        picking_tasks (
          id,
          pick_number,
          status,
          started_at,
          completed_at,
          has_shortages,
          shortage_details
        ),
        packing_tasks (
          id,
          status,
          started_at,
          completed_at,
          has_discrepancies
        )
      `)
      .order('created_at', { ascending: false })
      .limit(200);

    if (!error && orders) {
      // Get user profiles separately
      const userIds = [...new Set(orders.map(o => o.user_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name, email, phone')
        .in('id', userIds);

      const profileMap = {};
      profiles?.forEach(p => {
        profileMap[p.id] = p;
      });

      // Get addresses for zones and display
      const addressIds = [...new Set(orders.map(o => o.delivery_address_id).filter(Boolean))];
      const { data: addresses } = await supabase
        .from('addresses')
        .select('id, area, physical_address, complex_name, unit_number')
        .in('id', addressIds);

      const addressMap = {};
      addresses?.forEach(a => {
        addressMap[a.id] = a;
      });

      // Get box allocations for orders that have them
      const orderIdsWithBoxes = orders
        .filter(o => o.box_allocation_id)
        .map(o => o.box_allocation_id);

      let boxAllocationsMap = {};
      if (orderIdsWithBoxes.length > 0) {
        const { data: boxAllocations } = await supabase
          .from('box_allocations')
          .select(`
            id,
            order_id,
            allocated_at,
            delivery_boxes (
              box_number,
              current_zone
            )
          `)
          .in('id', orderIdsWithBoxes);

        boxAllocations?.forEach(ba => {
          boxAllocationsMap[ba.order_id] = ba;
        });
      }

      // Get delivery assignments for tracking delivery progress
      const orderIds = orders.map(o => o.id);
      const { data: deliveryAssignments } = await supabase
        .from('driver_assignments')
        .select(`
          id,
          order_id,
          status,
          assigned_at,
          collected_at,
          loaded_at,
          delivered_at,
          gatepass_issued_at,
          failure_reason,
          delivery_sessions (
            id,
            started_at,
            status
          )
        `)
        .in('order_id', orderIds);

      // Get order_status_history for accurate delivered timestamps
      const { data: statusHistory } = await supabase
        .from('order_status_history')
        .select('order_id, status, created_at')
        .in('order_id', orderIds)
        .eq('status', 'delivered');

      const deliveredAtMap = {};
      statusHistory?.forEach(sh => {
        deliveredAtMap[sh.order_id] = sh.created_at;
      });

      const deliveryMap = {};
      deliveryAssignments?.forEach(da => {
        deliveryMap[da.order_id] = da;
      });

      // Debug: Log first order to see box_allocations structure
      if (orders.length > 0) {
        console.log('Sample order data:', {
          orderNumber: orders[0].order_number,
          status: orders[0].status,
          box_allocation_id: orders[0].box_allocation_id,
          box_allocation: boxAllocationsMap[orders[0].id]
        });
      }

      // Transform into document flow format
      const documentFlows = orders.map(order => {
        const picking = order.picking_tasks?.[0] || null;
        const packing = order.packing_tasks?.[0] || null;
        const boxAllocation = boxAllocationsMap[order.id]; // Get from map
        const delivery = deliveryMap[order.id]; // Get delivery info
        const profile = profileMap[order.user_id];
        const address = addressMap[order.delivery_address_id];

        // Determine flow status
        let flowStatus = 'incomplete';
        let hasIssues = false;

        if (picking?.has_shortages || packing?.has_discrepancies || order.status === 'delivery_failed') {
          hasIssues = true;
        }

        // Only mark as complete when order is actually dispatched or completed
        if (order.status === 'delivered') {
          flowStatus = 'complete';
        } else if (order.status === 'delivery_failed') {
          flowStatus = 'failed';
        } else if (order.status === 'confirmed' || order.status === 'picking' || order.status === 'packing' || 
                   order.status === 'picking_complete' || order.status === 'packed' ||
                   order.status === 'ready_for_dispatch' || order.status === 'out_for_delivery') {
          flowStatus = 'in_progress';
        } else {
          flowStatus = 'incomplete';
        }

        // Delivery tracking stages
        const deliverySession = Array.isArray(delivery?.delivery_sessions) 
          ? delivery.delivery_sessions[0] 
          : delivery?.delivery_sessions;
        const collectedStatus = ['out_for_delivery', 'delivered', 'delivery_failed', 'delivery_complete'].includes(order.status)
                               ? 'completed' : 'not_started';
        // Priority: gatepass_issued_at > loaded_at > collected_at > assigned_at
        const collectedAt = delivery?.gatepass_issued_at || delivery?.loaded_at || 
                           delivery?.collected_at || deliverySession?.started_at ||
                           (collectedStatus === 'completed' ? delivery?.assigned_at : null);
        
        const inTransitStatus = order.status === 'out_for_delivery' ? 'in_progress' :
                               ['delivered', 'delivery_failed', 'delivery_complete'].includes(order.status) ? 'completed' : 'not_started';
        
        const deliveredStatus = ['delivered', 'delivery_complete'].includes(order.status) ? 'completed' :
                               order.status === 'delivery_failed' ? 'failed' : 'not_started';
        // Use order_status_history for accurate delivered time, fallback to delivery.delivered_at
        const deliveredAt = deliveredAtMap[order.id] || delivery?.delivered_at;
        const failureReason = delivery?.failure_reason;

        // Debug log for orders with delivery data
        if (delivery) {
          console.log(`Order ${order.order_number} delivery tracking:`, {
            orderStatus: order.status,
            deliveredStatus,
            inTransitStatus,
            collectedStatus,
            deliveredAt
          });
        }

        return {
          id: order.id,
          orderNumber: order.order_number,
          pickNumber: picking?.pick_number || null,
          packingId: packing?.id || null,
          orderStatus: order.status,
          orderDate: order.created_at,
          customerName: profile?.name || 'Unknown',
          customerPhone: profile?.phone || null,
          customerAddress: address ? 
            (() => {
              const parts = [];
              if (address.unit_number) parts.push(`Unit ${address.unit_number}`);
              if (address.complex_name) parts.push(address.complex_name);
              if (address.physical_address) parts.push(address.physical_address);
              if (address.area) parts.push(address.area);
              // Remove consecutive duplicates
              return parts.filter((part, idx) => part !== parts[idx - 1]).join(', ');
            })()
            : null,
          total: order.total,
          
          // Picking info
          pickingStatus: picking?.status || 'not_started',
          pickingStarted: picking?.started_at || null,
          pickingCompleted: picking?.completed_at || null,
          hasShortages: picking?.has_shortages || false,
          shortageCount: picking?.shortage_details?.length || 0,
          
          // Packing info
          packingStatus: packing?.status || 'not_started',
          packingStarted: packing?.started_at || null,
          packingCompleted: packing?.completed_at || null,
          hasDiscrepancies: packing?.has_discrepancies || false,
          
          // Box allocation info
          boxStatus: boxAllocation ? 'allocated' : 'not_started',
          boxNumber: boxAllocation?.delivery_boxes?.box_number || null,
          boxAllocatedAt: boxAllocation?.allocated_at || null,
          boxZone: boxAllocation?.delivery_boxes?.current_zone || address?.area || null,
          zone: address?.area || null,
          
          // Delivery tracking - NEW
          collectedStatus,
          collectedAt,
          inTransitStatus,
          deliveredStatus,
          deliveredAt,
          failureReason,
          
          // Flow info
          flowStatus,
          hasIssues
        };
      });

      setFlows(documentFlows);
      calculateStats(documentFlows);
    } else {
      console.error('Error loading document flows:', error);
    }

    setLoading(false);
  };

  const calculateStats = (flowsData) => {
    const stats = {
      total: flowsData.length,
      complete: flowsData.filter(f => f.flowStatus === 'complete').length,
      inProgress: flowsData.filter(f => f.flowStatus === 'in_progress').length,
      withIssues: flowsData.filter(f => f.hasIssues).length
    };
    setStats(stats);
  };

  const filterFlows = () => {
    let filtered = [...flows];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(flow =>
        flow.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flow.pickNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flow.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter === 'complete') {
      filtered = filtered.filter(f => f.flowStatus === 'complete');
    } else if (statusFilter === 'incomplete') {
      filtered = filtered.filter(f => f.flowStatus !== 'complete');
    } else if (statusFilter === 'issues') {
      filtered = filtered.filter(f => f.hasIssues);
    }

    setFilteredFlows(filtered);
  };

  const getStageColor = (status) => {
    if (status === 'completed' || status === 'allocated') return 'bg-green-500';
    if (status === 'in_progress') return 'bg-blue-500';
    if (status === 'failed') return 'bg-red-500';
    if (status === 'not_started') return 'bg-gray-300';
    return 'bg-gray-300';
  };

  const getStageIcon = (status) => {
    if (status === 'completed' || status === 'allocated') return <CheckCircle size={20} className="text-white" />;
    if (status === 'in_progress') return <Clock size={20} className="text-white" />;
    if (status === 'failed') return <XCircle size={20} className="text-white" />;
    return <Clock size={20} className="text-gray-500" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `R${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateDuration = (start, end) => {
    if (!start) return null;
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const diff = endTime - startTime;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Flow</h1>
              <p className="text-sm text-gray-600">Track order progression: Order → Pick → Pack → Box → Delivery</p>
            </div>
            
            <button
              onClick={loadDocumentFlows}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <div className="text-xs text-blue-600 font-medium mb-1">TOTAL FLOWS</div>
              <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <div className="text-xs text-green-600 font-medium mb-1">COMPLETE</div>
              <div className="text-2xl font-bold text-green-900">{stats.complete}</div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
              <div className="text-xs text-orange-600 font-medium mb-1">IN PROGRESS</div>
              <div className="text-2xl font-bold text-orange-900">{stats.inProgress}</div>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
              <div className="text-xs text-red-600 font-medium mb-1">WITH ISSUES</div>
              <div className="text-2xl font-bold text-red-900">{stats.withIssues}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="px-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search order, pick number, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Flows</option>
              <option value="complete">Complete Only</option>
              <option value="incomplete">Incomplete Only</option>
              <option value="issues">With Issues</option>
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>

          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredFlows.length} of {flows.length} document flows
          </div>
        </div>
      </div>

      {/* Document Flow Cards */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document flows...</p>
          </div>
        ) : filteredFlows.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FileText size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No document flows found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredFlows.map((flow) => (
              <div key={flow.id} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="p-6">
                  {/* Header Row */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-gray-900">{flow.orderNumber}</h3>
                        {flow.hasIssues && (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full shadow-sm ring-1 ring-orange-200">
                            <AlertCircle size={12} />
                            Issues
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${
                          flow.flowStatus === 'complete' 
                            ? 'bg-green-100 text-green-700 ring-1 ring-green-200'
                            : flow.flowStatus === 'in_progress'
                            ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-200'
                            : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                        }`}>
                          {flow.flowStatus === 'complete' ? 'Complete' : flow.flowStatus === 'in_progress' ? 'In Progress' : 'Pending'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {flow.customerName}{flow.customerPhone ? ` • ${flow.customerPhone}` : ''} • {formatDate(flow.orderDate)} • {formatCurrency(flow.total)}
                      </div>
                      {flow.customerAddress && (
                        <div className="text-sm text-gray-500 mt-1">
                          📍 {flow.customerAddress}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Flow Visualization */}
                  <div className="flex items-center gap-2">
                    {/* Stage 1: Order */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor('completed')}`}>
                          <ShoppingCart size={20} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Order Created</div>
                          <div className="text-xs text-gray-500">{flow.orderNumber}</div>
                          <div className="text-xs text-gray-500">{formatDate(flow.orderDate)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 2: Picking */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.pickingStatus)}`}>
                          {getStageIcon(flow.pickingStatus)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {flow.pickingStatus === 'completed' ? 'Picked' : 'Picking'}
                          </div>
                          {flow.pickNumber ? (
                            <>
                              <div className="text-xs text-gray-500">{flow.pickNumber}</div>
                              {flow.pickingCompleted && (
                                <>
                                  <div className="text-xs text-green-600">
                                    ✓ {calculateDuration(flow.pickingStarted, flow.pickingCompleted)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {new Date(flow.pickingCompleted).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </>
                              )}
                              {flow.pickingStatus === 'in_progress' && (
                                <div className="text-xs text-blue-600">
                                  ⏱ {calculateDuration(flow.pickingStarted, null)}
                                </div>
                              )}
                              {flow.hasShortages && (
                                <div className="text-xs text-orange-600">
                                  ⚠️ {flow.shortageCount} shortage{flow.shortageCount !== 1 ? 's' : ''}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">Not started</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 3: Packing */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.packingStatus)}`}>
                          {getStageIcon(flow.packingStatus)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {flow.packingStatus === 'completed' ? 'Packed' : 'Packing'}
                          </div>
                          {flow.packingStatus !== 'not_started' ? (
                            <>
                              <div className="text-xs text-gray-500">Packing Task</div>
                              {flow.packingCompleted && (
                                <>
                                  <div className="text-xs text-green-600">
                                    ✓ {calculateDuration(flow.packingStarted, flow.packingCompleted)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {new Date(flow.packingCompleted).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </>
                              )}
                              {flow.packingStatus === 'in_progress' && (
                                <div className="text-xs text-blue-600">
                                  ⏱ {calculateDuration(flow.packingStarted, null)}
                                </div>
                              )}
                              {flow.hasDiscrepancies && (
                                <div className="text-xs text-red-600">
                                  ⚠️ Has discrepancies
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">Not started</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 4: Box Allocation */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.boxStatus)}`}>
                          {getStageIcon(flow.boxStatus)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {flow.boxStatus === 'allocated' ? 'Box Allocated' : 'Box Allocation'}
                          </div>
                          {flow.boxStatus === 'allocated' ? (
                            <>
                              <div className="text-xs text-gray-500">Box {flow.boxNumber}</div>
                              {flow.boxZone && (
                                <div className="text-xs text-purple-600 font-medium">
                                  Zone: {flow.boxZone}
                                </div>
                              )}
                              {flow.boxAllocatedAt && (
                                <div className="text-xs text-green-600">
                                  ✓ {new Date(flow.boxAllocatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">Not allocated</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 5: Collected by Driver - NEW */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.collectedStatus)}`}>
                          <Truck size={20} className={flow.collectedStatus === 'completed' ? 'text-white' : 'text-gray-500'} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Collected</div>
                          {flow.collectedStatus === 'completed' ? (
                            <>
                              <div className="text-xs text-gray-500">By Driver</div>
                              {flow.collectedAt && (
                                <div className="text-xs text-green-600">
                                  ✓ {new Date(flow.collectedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">Awaiting pickup</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 6: In Transit - NEW */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.inTransitStatus)}`}>
                          {flow.inTransitStatus === 'in_progress' ? (
                            <Navigation size={20} className="text-white animate-pulse" />
                          ) : flow.inTransitStatus === 'completed' ? (
                            <CheckCircle size={20} className="text-white" />
                          ) : (
                            <Clock size={20} className="text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">In Transit</div>
                          {flow.inTransitStatus === 'in_progress' ? (
                            <>
                              <div className="text-xs text-blue-600">⏱ En route</div>
                              {flow.collectedAt && (
                                <div className="text-xs text-gray-500">
                                  {calculateDuration(flow.collectedAt, null)}
                                </div>
                              )}
                            </>
                          ) : flow.inTransitStatus === 'completed' ? (
                            <div className="text-xs text-green-600">✓ Completed</div>
                          ) : (
                            <div className="text-xs text-gray-400">Not started</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight size={24} className="text-gray-400 flex-shrink-0" />

                    {/* Stage 7: Delivered - NEW */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStageColor(flow.deliveredStatus)}`}>
                          {flow.deliveredStatus === 'failed' ? (
                            <XCircle size={20} className="text-white" />
                          ) : flow.deliveredStatus === 'completed' ? (
                            <MapPin size={20} className="text-white" />
                          ) : (
                            <Clock size={20} className="text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {flow.deliveredStatus === 'failed' ? 'Delivery Failed' : 'Delivered'}
                          </div>
                          {flow.deliveredStatus === 'completed' ? (
                            <>
                              <div className="text-xs text-green-600">✓ Delivered</div>
                              {flow.deliveredAt && (
                                <div className="text-xs text-gray-500">
                                  {new Date(flow.deliveredAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </>
                          ) : flow.deliveredStatus === 'failed' ? (
                            <>
                              <div className="text-xs text-red-600">✗ Failed</div>
                              {flow.failureReason && (
                                <div className="text-xs text-gray-500">
                                  {flow.failureReason}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-400">Pending</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Status Badge at Bottom */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Current Status: <span className="font-medium text-gray-900">{flow.orderStatus}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentFlowDashboard;
